// app/frontend/pages/Community/Index.tsx
import { useState, useRef, useEffect, useCallback } from "react";
import { Link, router, usePage } from "@inertiajs/react";
import {
  Box,
  Text,
  Card,
  Group,
  Stack,
  Avatar,
  NavLink,
  ScrollArea,
  Divider,
  ActionIcon,
  Drawer,
  Burger,
  Modal,
  TextInput,
  Textarea,
  Button,
  Select,
  UnstyledButton,
  Popover,
  Image,
  CloseButton,
  Skeleton,
  Loader,
} from "@mantine/core";
import { useDisclosure, useMediaQuery, useDebouncedValue } from "@mantine/hooks";
import {
  IconMessageCircle,
  IconPin,
  IconLayoutList,
  IconBell,
  IconVideo,
  IconPlus,
  IconHash,
  IconLink,
  IconPlayerPlay,
  IconGif,
  IconPhoto,
  IconMoodSmile,
  IconChartBar,
  IconMicrophone,
  IconX,
  IconExternalLink,
} from "@tabler/icons-react";
import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import { SEO } from "../../components/common/SEO";
import { L2KText, L2KH4, L2KCaption, L2KBadge } from "../../components/ui/Typography";
import { PostContent } from "../../components/community/PostContent";
import { ComingSoonPageOverlay } from "../../components/videos/ComingSoonPageOverlay";
import HybridLayout from "../../components/layouts/HybridLayout";

// Link Preview types
interface LinkPreview {
  url: string;
  title: string | null;
  description: string | null;
  image: string | null;
  site_name: string | null;
  domain: string | null;
  is_video: boolean;
  loading?: boolean;
  error?: boolean;
}

// URL detection regex
const URL_REGEX = /https?:\/\/[^\s<>"\[\]]+/gi;

interface User {
  id: number;
  name: string;
  username: string;
  avatar_url: string | null;
}

interface CurrentUser {
  id: number;
  name: string;
  email: string;
  avatar_url: string | null;
  admin: boolean;
}

interface Space {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  emoji: string;
  posts_count: number;
  category: string | null;
}

interface PostSpace {
  id: number;
  name: string;
  slug: string;
  emoji: string;
}

interface Post {
  id: number;
  title: string;
  body: string;
  pinned: boolean;
  comments_count: number;
  reactions_count: number;
  last_activity_at: string;
  created_at: string;
  user: User;
  user_reaction?: string | null;
  space?: PostSpace | null;
}

interface Props {
  spaces: Space[];
  spaces_by_category: Record<string, Space[]>;
  posts: Post[];
  unread_count: number;
  current_user: CurrentUser;
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return "just now";
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return date.toLocaleDateString();
}

interface CreatePostModalProps {
  opened: boolean;
  onClose: () => void;
  spaces: Space[];
  currentUser: CurrentUser;
}

interface UploadedImage {
  file: File;
  preview: string;
}

// Link Preview Card Component
function LinkPreviewCard({
  preview,
  onRemove
}: {
  preview: LinkPreview;
  onRemove: () => void;
}) {
  if (preview.loading) {
    return (
      <Card
        padding="sm"
        radius="md"
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.03)",
          border: "1px solid rgba(255, 255, 255, 0.08)",
        }}
      >
        <Group gap="sm" align="center">
          <Loader size="sm" color="orange" />
          <L2KCaption variant="tertiary">Loading preview...</L2KCaption>
        </Group>
      </Card>
    );
  }

  if (preview.error) {
    return null;
  }

  return (
    <Card
      padding={0}
      radius="md"
      style={{
        backgroundColor: "rgba(255, 255, 255, 0.03)",
        border: "1px solid rgba(255, 255, 255, 0.08)",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <ActionIcon
        size="xs"
        color="dark"
        variant="filled"
        style={{
          position: "absolute",
          top: 8,
          right: 8,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          zIndex: 10,
        }}
        onClick={onRemove}
      >
        <IconX size={12} />
      </ActionIcon>

      <Group gap={0} wrap="nowrap" align="stretch">
        {/* 16:9 Thumbnail (160x90) */}
        {preview.image && (
          <Box
            style={{
              position: "relative",
              width: 160,
              height: 90,
              flexShrink: 0,
              overflow: "hidden",
            }}
          >
            <Image
              src={preview.image}
              alt={preview.title || "Link preview"}
              width={160}
              height={90}
              fit="cover"
            />
            {preview.is_video && (
              <Box
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  borderRadius: "50%",
                  width: 32,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <IconPlayerPlay size={16} color="#fff" style={{ marginLeft: 2 }} />
              </Box>
            )}
          </Box>
        )}

        {/* Content */}
        <Box p="sm" style={{ flex: 1, minWidth: 0 }}>
          <Stack gap={4}>
            <L2KCaption variant="tertiary" style={{ fontSize: 10 }}>
              {preview.site_name || preview.domain}
            </L2KCaption>
            {preview.title && (
              <L2KText size="xs" style={{ fontWeight: 500, lineHeight: 1.3 }}>
                {preview.title.length > 60 ? preview.title.slice(0, 60) + "..." : preview.title}
              </L2KText>
            )}
            {preview.description && (
              <L2KCaption variant="tertiary" style={{
                fontSize: 11,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}>
                {preview.description}
              </L2KCaption>
            )}
          </Stack>
        </Box>
      </Group>
    </Card>
  );
}

function CreatePostModal({ opened, onClose, spaces, currentUser }: CreatePostModalProps) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [selectedSpace, setSelectedSpace] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emojiPickerOpened, setEmojiPickerOpened] = useState(false);
  const [linkPopoverOpened, setLinkPopoverOpened] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");
  const [images, setImages] = useState<UploadedImage[]>([]);
  const [linkPreviews, setLinkPreviews] = useState<LinkPreview[]>([]);
  const [fetchedUrls, setFetchedUrls] = useState<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Debounce body text for URL detection
  const [debouncedBody] = useDebouncedValue(body, 500);

  const spaceOptions = spaces.map((space) => ({
    value: space.slug,
    label: `${space.emoji} ${space.name}`,
  }));

  // Fetch link preview from API
  const fetchLinkPreview = useCallback(async (url: string) => {
    if (fetchedUrls.has(url)) return;

    // Add loading state
    setLinkPreviews((prev) => [...prev, { url, loading: true } as LinkPreview]);
    setFetchedUrls((prev) => new Set(prev).add(url));

    try {
      const response = await fetch(`/api/link_preview?url=${encodeURIComponent(url)}`);
      if (response.ok) {
        const data = await response.json();
        setLinkPreviews((prev) =>
          prev.map((p) => (p.url === url ? { ...data, loading: false } : p))
        );
      } else {
        // Remove loading preview on error
        setLinkPreviews((prev) => prev.filter((p) => p.url !== url));
      }
    } catch {
      // Remove loading preview on error
      setLinkPreviews((prev) => prev.filter((p) => p.url !== url));
    }
  }, [fetchedUrls]);

  // Detect URLs in body and fetch previews
  useEffect(() => {
    const urls = debouncedBody.match(URL_REGEX) || [];
    const uniqueUrls = [...new Set(urls)];

    uniqueUrls.forEach((url) => {
      if (!fetchedUrls.has(url)) {
        fetchLinkPreview(url);
      }
    });
  }, [debouncedBody, fetchLinkPreview, fetchedUrls]);

  const removeLinkPreview = (url: string) => {
    setLinkPreviews((prev) => prev.filter((p) => p.url !== url));
    setFetchedUrls((prev) => {
      const next = new Set(prev);
      next.delete(url);
      return next;
    });
  };

  const handleEmojiSelect = (emoji: { native: string }) => {
    setBody((prev) => prev + emoji.native);
    setEmojiPickerOpened(false);
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const newImages: UploadedImage[] = [];
    Array.from(files).forEach((file) => {
      if (file.type.startsWith("image/")) {
        const preview = URL.createObjectURL(file);
        newImages.push({ file, preview });
      }
    });
    setImages((prev) => [...prev, ...newImages]);

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeImage = (index: number) => {
    setImages((prev) => {
      const newImages = [...prev];
      URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1);
      return newImages;
    });
  };

  const insertLink = () => {
    if (!linkUrl.trim()) return;
    // Just paste the URL directly - it will auto-preview
    setBody((prev) => prev + (prev ? "\n" : "") + linkUrl);
    setLinkUrl("");
    setLinkText("");
    setLinkPopoverOpened(false);
  };

  const handleSubmit = () => {
    if (!body.trim() || !selectedSpace) return;

    setIsSubmitting(true);

    // For now, just submit without images (would need FormData for actual upload)
    router.post(
      `/community/spaces/${selectedSpace}/posts`,
      { post: { title: title.trim() || body.slice(0, 50), body } },
      {
        onSuccess: () => {
          setTitle("");
          setBody("");
          setSelectedSpace(null);
          images.forEach((img) => URL.revokeObjectURL(img.preview));
          setImages([]);
          setLinkPreviews([]);
          setFetchedUrls(new Set());
          onClose();
        },
        onFinish: () => setIsSubmitting(false),
      }
    );
  };

  const handleClose = () => {
    setTitle("");
    setBody("");
    setSelectedSpace(null);
    setLinkUrl("");
    setLinkText("");
    images.forEach((img) => URL.revokeObjectURL(img.preview));
    setImages([]);
    setLinkPreviews([]);
    setFetchedUrls(new Set());
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={<L2KText size="sm" style={{ fontWeight: 600 }}>Create post</L2KText>}
      size="lg"
      centered
      styles={{
        content: {
          backgroundColor: "rgba(26, 26, 26, 0.95)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          backdropFilter: "blur(20px)",
        },
        header: {
          backgroundColor: "transparent",
          borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
        },
        title: {
          color: "#F0F0F0",
        },
        close: {
          color: "#B8B8B8",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.05)",
          },
        },
        body: {
          padding: "1rem",
        },
      }}
    >
      <Stack gap="md">
        <TextInput
          placeholder="Title (optional)"
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
          styles={{
            input: {
              backgroundColor: "rgba(255, 255, 255, 0.03)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              color: "#e5e5e5",
              fontSize: "14px",
              "&:focus": {
                borderColor: "rgba(255, 152, 0, 0.5)",
              },
            },
          }}
        />

        <Textarea
          ref={textareaRef}
          placeholder="Write something..."
          value={body}
          onChange={(e) => setBody(e.currentTarget.value)}
          minRows={4}
          autosize
          styles={{
            input: {
              backgroundColor: "rgba(255, 255, 255, 0.03)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              color: "#e5e5e5",
              fontSize: "14px",
              "&:focus": {
                borderColor: "rgba(255, 152, 0, 0.5)",
              },
            },
          }}
        />

        {/* Image Previews */}
        {images.length > 0 && (
          <Group gap="xs">
            {images.map((img, index) => (
              <Box
                key={index}
                style={{
                  position: "relative",
                  borderRadius: 8,
                  overflow: "hidden",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                }}
              >
                <Image
                  src={img.preview}
                  alt={`Upload ${index + 1}`}
                  width={80}
                  height={80}
                  fit="cover"
                />
                <ActionIcon
                  size="xs"
                  color="dark"
                  variant="filled"
                  style={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    backgroundColor: "rgba(0, 0, 0, 0.7)",
                  }}
                  onClick={() => removeImage(index)}
                >
                  <IconX size={12} />
                </ActionIcon>
              </Box>
            ))}
          </Group>
        )}

        {/* Link Previews */}
        {linkPreviews.length > 0 && (
          <Stack gap="xs">
            {linkPreviews.map((preview) => (
              <LinkPreviewCard
                key={preview.url}
                preview={preview}
                onRemove={() => removeLinkPreview(preview.url)}
              />
            ))}
          </Stack>
        )}

        {/* Hidden file input */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleImageUpload}
          accept="image/*"
          multiple
          style={{ display: "none" }}
        />

        {/* Media Toolbar */}
        <Group gap={4}>
          <ActionIcon variant="subtle" size="lg" color="gray" title="Add hashtag" onClick={() => setBody((prev) => prev + "#")}>
            <IconHash size={18} />
          </ActionIcon>

          <Popover
            opened={linkPopoverOpened}
            onChange={setLinkPopoverOpened}
            position="top"
            withArrow
            shadow="md"
          >
            <Popover.Target>
              <ActionIcon
                variant="subtle"
                size="lg"
                color="gray"
                title="Add link"
                onClick={() => setLinkPopoverOpened((o) => !o)}
              >
                <IconLink size={18} />
              </ActionIcon>
            </Popover.Target>
            <Popover.Dropdown
              style={{
                backgroundColor: "rgba(26, 26, 26, 0.98)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(20px)",
              }}
            >
              <Stack gap="xs" style={{ width: 280 }}>
                <L2KCaption variant="tertiary">
                  Paste a URL to embed with preview
                </L2KCaption>
                <TextInput
                  placeholder="https://..."
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.currentTarget.value)}
                  size="xs"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      insertLink();
                    }
                  }}
                  styles={{
                    input: {
                      backgroundColor: "rgba(255, 255, 255, 0.05)",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      color: "#e5e5e5",
                    },
                  }}
                />
                <Button
                  size="xs"
                  onClick={insertLink}
                  disabled={!linkUrl.trim()}
                  leftSection={<IconExternalLink size={14} />}
                  style={{
                    background: "linear-gradient(135deg, #f97316, #ea580c)",
                  }}
                >
                  Add Link
                </Button>
              </Stack>
            </Popover.Dropdown>
          </Popover>

          <ActionIcon variant="subtle" size="lg" color="gray" title="Add video" onClick={() => setBody((prev) => prev + "\n[Video: ]")}>
            <IconPlayerPlay size={18} />
          </ActionIcon>

          <ActionIcon variant="subtle" size="lg" color="gray" title="Add GIF (coming soon)" disabled>
            <IconGif size={18} />
          </ActionIcon>

          <ActionIcon
            variant="subtle"
            size="lg"
            color="gray"
            title="Add image"
            onClick={() => fileInputRef.current?.click()}
          >
            <IconPhoto size={18} />
          </ActionIcon>

          <Popover
            opened={emojiPickerOpened}
            onChange={setEmojiPickerOpened}
            position="top"
            withArrow
            shadow="md"
          >
            <Popover.Target>
              <ActionIcon
                variant="subtle"
                size="lg"
                color="gray"
                title="Add emoji"
                onClick={() => setEmojiPickerOpened((o) => !o)}
              >
                <IconMoodSmile size={18} />
              </ActionIcon>
            </Popover.Target>
            <Popover.Dropdown
              style={{
                backgroundColor: "transparent",
                border: "none",
                padding: 0,
              }}
            >
              <Picker
                data={data}
                onEmojiSelect={handleEmojiSelect}
                theme="dark"
                previewPosition="none"
                skinTonePosition="none"
              />
            </Popover.Dropdown>
          </Popover>

          <ActionIcon variant="subtle" size="lg" color="gray" title="Add poll (coming soon)" disabled>
            <IconChartBar size={18} />
          </ActionIcon>

          <ActionIcon variant="subtle" size="lg" color="gray" title="Voice note (coming soon)" disabled>
            <IconMicrophone size={18} />
          </ActionIcon>
        </Group>

        <Divider color="rgba(255, 255, 255, 0.08)" />

        <Group justify="space-between" align="center">
          <Select
            placeholder="Choose a space to post in"
            data={spaceOptions}
            value={selectedSpace}
            onChange={setSelectedSpace}
            searchable
            styles={{
              input: {
                backgroundColor: "rgba(255, 255, 255, 0.03)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                color: "#e5e5e5",
                fontSize: "13px",
                minWidth: 200,
                "&:focus": {
                  borderColor: "rgba(255, 152, 0, 0.5)",
                },
              },
              dropdown: {
                backgroundColor: "rgba(26, 26, 26, 0.98)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(20px)",
              },
              option: {
                color: "#e5e5e5",
                fontSize: "13px",
                "&[data-selected]": {
                  backgroundColor: "rgba(255, 152, 0, 0.15)",
                  color: "#FF9800",
                },
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                },
              },
            }}
          />

          <Button
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={!body.trim() || !selectedSpace}
            styles={{
              root: {
                background: (!body.trim() || !selectedSpace) ? "rgba(255, 255, 255, 0.08)" : "linear-gradient(135deg, #f97316, #ea580c)",
                border: (!body.trim() || !selectedSpace) ? "1px solid rgba(255, 255, 255, 0.1)" : "none",
                color: (!body.trim() || !selectedSpace) ? "#555" : "#fff",
                "&:disabled": {
                  background: "rgba(255, 255, 255, 0.08)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  color: "#555",
                },
              },
            }}
          >
            Publish
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

interface StartPostInputProps {
  currentUser: CurrentUser;
  onClick: () => void;
}

function StartPostInput({ currentUser, onClick }: StartPostInputProps) {
  return (
    <UnstyledButton
      onClick={onClick}
      style={{
        width: "100%",
        backgroundColor: "rgba(255, 255, 255, 0.03)",
        border: "1px solid rgba(255, 255, 255, 0.08)",
        backdropFilter: "blur(10px)",
        borderRadius: 8,
        padding: "12px 16px",
        transition: "all 0.2s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "rgba(255, 152, 0, 0.3)";
        e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.05)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.08)";
        e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.03)";
      }}
    >
      <Group justify="space-between" align="center">
        <Group gap="sm">
          <Avatar
            src={currentUser.avatar_url}
            alt={currentUser.name}
            radius="xl"
            size="sm"
            color="orange"
          >
            {currentUser.name.charAt(0)}
          </Avatar>
          <L2KText size="xs" variant="tertiary">
            Start a post...
          </L2KText>
        </Group>
        <ActionIcon
          variant="subtle"
          size="sm"
          style={{ color: "#B8B8B8" }}
        >
          <IconPlus size={16} />
        </ActionIcon>
      </Group>
    </UnstyledButton>
  );
}

function PostCard({ post }: { post: Post }) {
  return (
    <Link
      href={`/community/spaces/${post.space?.slug}/posts/${post.id}`}
      style={{ textDecoration: "none" }}
    >
      <Card
        padding="md"
        radius="md"
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.03)",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          backdropFilter: "blur(10px)",
          cursor: "pointer",
          transition: "all 0.2s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = "rgba(255, 152, 0, 0.3)";
          e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.05)";
          e.currentTarget.style.boxShadow = "0 8px 32px rgba(0, 0, 0, 0.2)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.08)";
          e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.03)";
          e.currentTarget.style.boxShadow = "none";
        }}
      >
        <Stack gap="xs">
          {/* Post Header */}
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Group gap="sm" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
              <Avatar
                src={post.user.avatar_url}
                alt={post.user.name}
                radius="xl"
                size="sm"
                color="orange"
                style={{ flexShrink: 0 }}
              >
                {post.user.name.charAt(0)}
              </Avatar>
              <Box style={{ minWidth: 0, flex: 1 }}>
                <Group gap="xs" wrap="wrap">
                  <L2KText size="xs" style={{ fontWeight: 500 }}>
                    {post.user.name}
                  </L2KText>
                  {post.space && (
                    <>
                      <L2KCaption variant="tertiary" visibleFrom="xs">in</L2KCaption>
                      <L2KText size="xs" variant="accent" style={{ fontWeight: 500 }}>
                        {post.space.emoji} {post.space.name}
                      </L2KText>
                    </>
                  )}
                </Group>
                <L2KCaption variant="tertiary">
                  {formatRelativeTime(post.created_at)}
                </L2KCaption>
              </Box>
            </Group>
            {post.pinned && (
              <L2KBadge
                variant="subtle"
                size="xs"
                leftSection={<IconPin size={10} />}
                style={{ flexShrink: 0 }}
              >
                Pinned
              </L2KBadge>
            )}
          </Group>

          {/* Post Content */}
          <Box>
            <L2KText size="sm" style={{ fontWeight: 600, marginBottom: 4 }}>
              {post.title}
            </L2KText>
            <PostContent body={post.body} size="xs" maxPreviews={1} />
          </Box>

          {/* Post Footer */}
          <Group gap="md" mt={4}>
            <Group gap={4}>
              <IconMessageCircle size={14} color="#B8B8B8" />
              <L2KCaption variant="tertiary">
                {post.comments_count} {post.comments_count === 1 ? "comment" : "comments"}
              </L2KCaption>
            </Group>
            {post.reactions_count > 0 && (
              <L2KCaption variant="tertiary">
                {post.reactions_count} reactions
              </L2KCaption>
            )}
          </Group>
        </Stack>
      </Card>
    </Link>
  );
}

interface SidebarContentProps {
  spaces_by_category: Record<string, Space[]>;
  currentUser: CurrentUser;
  onNavClick?: () => void;
}

function SidebarContent({ spaces_by_category, currentUser, onNavClick }: SidebarContentProps) {
  const categoryOrder = ["Welcome", "Community", "Resources"];

  return (
    <Box
      style={{
        backgroundColor: "#1a1a1a",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Logo/Header */}
      <Box px="md" py="lg" style={{ borderBottom: "1px solid #2a2a2a" }}>
        <Link href="/" style={{ textDecoration: "none" }} onClick={onNavClick}>
          <Text
            size="xl"
            fw={700}
            style={{
              background: "linear-gradient(135deg, #f97316, #ea580c)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Learntokiz
          </Text>
        </Link>
      </Box>

      {/* Navigation */}
      <ScrollArea flex={1} px="sm" py="md">
        <Stack gap={4}>
          {/* Back to Learning */}
          <NavLink
            component={Link}
            href="/videos"
            label="Learn"
            leftSection={<IconVideo size={18} />}
            onClick={onNavClick}
            styles={{
              root: {
                borderRadius: 8,
                fontWeight: 500,
                color: "#e5e5e5",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                },
              },
              label: {
                color: "#e5e5e5",
              },
            }}
          />

          <Divider my="sm" color="#2a2a2a" />

          {/* Feed Link */}
          <NavLink
            component={Link}
            href="/community"
            label="Feed"
            leftSection={<IconLayoutList size={18} />}
            active
            onClick={onNavClick}
            styles={{
              root: {
                borderRadius: 8,
                fontWeight: 500,
                color: "#e5e5e5",
                "&[data-active]": {
                  backgroundColor: "rgba(249, 115, 22, 0.15)",
                  color: "#f97316",
                },
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                },
              },
              label: {
                color: "inherit",
              },
            }}
          />

          <Divider my="sm" color="#2a2a2a" />

          {/* Spaces by Category */}
          {categoryOrder.map((category) => {
            const categorySpaces = spaces_by_category[category];
            if (!categorySpaces || categorySpaces.length === 0) return null;

            return (
              <Box key={category} mb="md">
                <Text
                  size="xs"
                  fw={600}
                  c="#737373"
                  tt="uppercase"
                  px="sm"
                  mb="xs"
                >
                  {category}
                </Text>
                {categorySpaces.map((space) => (
                  <NavLink
                    key={space.id}
                    component={Link}
                    href={`/community/spaces/${space.slug}`}
                    label={space.name}
                    leftSection={<Text size="sm">{space.emoji}</Text>}
                    onClick={onNavClick}
                    styles={{
                      root: {
                        borderRadius: 8,
                        color: "#e5e5e5",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.05)",
                        },
                      },
                      label: {
                        color: "#e5e5e5",
                      },
                    }}
                  />
                ))}
              </Box>
            );
          })}
        </Stack>
      </ScrollArea>

      {/* User Section */}
      <Box px="md" py="md" style={{ borderTop: "1px solid #2a2a2a" }}>
        <Group>
          <Avatar
            src={currentUser.avatar_url}
            alt={currentUser.name}
            radius="xl"
            size="sm"
            color="orange"
          >
            {currentUser.name.charAt(0)}
          </Avatar>
          <Box flex={1} style={{ minWidth: 0 }}>
            <L2KText size="xs" style={{ fontWeight: 500 }}>
              {currentUser.name}
            </L2KText>
          </Box>
        </Group>
      </Box>
    </Box>
  );
}

export default function CommunityIndex({ spaces, spaces_by_category, posts, unread_count, current_user }: Props) {
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);
  const [postModalOpened, { open: openPostModal, close: closePostModal }] = useDisclosure(false);
  const isDesktop = useMediaQuery("(min-width: 992px)");
  const { coming_soon } = usePage().props as { coming_soon?: boolean };

  if (coming_soon) {
    return (
      <HybridLayout>
        <SEO
        title="Community — Connect with Urbankiz Dancers Worldwide | Learntokiz"
        description="Join the Learntokiz community. Share moves, ask questions, swap videos, and learn alongside dancers from every level and city."
      />
        <ComingSoonPageOverlay
          title="Community Launching Soon"
          description="The Learntokiz community is being staged for the 2.0 launch. Check back soon — or catch up on the blog in the meantime."
        >
          <Box style={{ minHeight: "100vh", background: "#171717" }} />
        </ComingSoonPageOverlay>
      </HybridLayout>
    );
  }

  return (
    <Box
      style={{
        minHeight: "100vh",
        background: `
          radial-gradient(ellipse at 20% 50%, rgba(255, 152, 0, 0.15) 0%, transparent 50%),
          radial-gradient(ellipse at 80% 20%, rgba(255, 193, 7, 0.12) 0%, transparent 50%),
          radial-gradient(ellipse at 40% 80%, rgba(255, 87, 34, 0.1) 0%, transparent 50%),
          linear-gradient(135deg, #171717 0%, #1a1a1a 25%, #171717 50%, #1f1f1f 75%, #171717 100%)
        `,
      }}
    >
      <SEO
        title="Community — Connect with Urbankiz Dancers Worldwide | Learntokiz"
        description="Join the Learntokiz community. Share moves, ask questions, swap videos, and learn alongside dancers from every level and city."
      />

      {/* Mobile Drawer */}
      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        size={280}
        padding={0}
        withCloseButton={false}
        styles={{
          body: {
            padding: 0,
            height: "100%",
          },
          content: {
            backgroundColor: "#1a1a1a",
          },
        }}
      >
        <SidebarContent
          spaces_by_category={spaces_by_category}
          currentUser={current_user}
          onNavClick={closeDrawer}
        />
      </Drawer>

      {/* Desktop Sidebar */}
      {isDesktop && (
        <Box
          style={{
            width: 280,
            position: "fixed",
            left: 0,
            top: 0,
            height: "100vh",
            borderRight: "1px solid #2a2a2a",
            zIndex: 100,
          }}
        >
          <SidebarContent
            spaces_by_category={spaces_by_category}
            currentUser={current_user}
          />
        </Box>
      )}

      {/* Mobile Header */}
      {!isDesktop && (
        <Box
          px="md"
          py="sm"
          style={{
            backgroundColor: "#1a1a1a",
            borderBottom: "1px solid #2a2a2a",
            position: "sticky",
            top: 0,
            zIndex: 50,
          }}
        >
          <Group justify="space-between" align="center">
            <Group gap="sm">
              <Burger
                opened={drawerOpened}
                onClick={openDrawer}
                size="sm"
                color="#e5e5e5"
              />
              <Link href="/" style={{ textDecoration: "none" }}>
                <Text
                  size="lg"
                  fw={700}
                  style={{
                    background: "linear-gradient(135deg, #f97316, #ea580c)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Learntokiz
                </Text>
              </Link>
            </Group>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="lg"
              style={{ color: "#a3a3a3" }}
            >
              <IconBell size={20} />
            </ActionIcon>
          </Group>
        </Box>
      )}

      {/* Main Content */}
      <Box
        style={{
          marginLeft: isDesktop ? 280 : 0,
          padding: isDesktop ? "2rem" : "1rem",
          maxWidth: isDesktop ? 800 : "100%",
        }}
      >
        <Stack gap="lg">
          {/* Header - Desktop only (mobile has sticky header) */}
          {isDesktop && (
            <>
              <Group justify="space-between" align="center">
                <Box>
                  <Group gap="sm" align="center">
                    <IconLayoutList size={22} color="#FF9800" />
                    <L2KH4>Feed</L2KH4>
                  </Group>
                  <L2KText size="xs" variant="tertiary" style={{ marginTop: 4 }}>
                    Recent posts from all spaces
                  </L2KText>
                </Box>
                <Group gap="xs">
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="lg"
                    style={{ color: "#a3a3a3" }}
                  >
                    <IconBell size={20} />
                  </ActionIcon>
                </Group>
              </Group>
              <Divider color="#2a2a2a" />
            </>
          )}

          {/* Mobile Page Title */}
          {!isDesktop && (
            <Box>
              <Group gap="xs" align="center">
                <IconLayoutList size={18} color="#FF9800" />
                <L2KText size="sm" style={{ fontWeight: 600 }}>Feed</L2KText>
              </Group>
              <L2KCaption variant="tertiary" style={{ marginTop: 2 }}>
                Recent posts from all spaces
              </L2KCaption>
            </Box>
          )}

          {/* Start Post Input */}
          <StartPostInput currentUser={current_user} onClick={openPostModal} />

          {/* Posts Feed */}
          <Stack gap="sm">
            {posts.length === 0 ? (
              <Card
                padding="xl"
                radius="md"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.03)",
                  border: "1px solid rgba(255, 255, 255, 0.08)",
                  backdropFilter: "blur(10px)",
                  textAlign: "center",
                }}
              >
                <L2KText size="sm" variant="tertiary">
                  No posts yet. Be the first to start a discussion!
                </L2KText>
              </Card>
            ) : (
              posts.map((post) => <PostCard key={post.id} post={post} />)
            )}
          </Stack>
        </Stack>
      </Box>

      {/* Create Post Modal */}
      <CreatePostModal
        opened={postModalOpened}
        onClose={closePostModal}
        spaces={spaces}
        currentUser={current_user}
      />
    </Box>
  );
}
